import { useMemo } from 'react'
import type { Task } from '@/src/renderer/lib/db/models'
import { useOptimisticEntityOverrides } from './useOptimisticEntityOverrides'

/**
 * Task-typed wrapper around `useOptimisticEntityOverrides`. Kept as a
 * named hook so the existing call sites (UpcomingPage, ProjectPage,
 * QuickNotesPage) stay readable and can keep their original variable
 * names.
 *
 * See `useOptimisticEntityOverrides` for cleanup semantics — this hook
 * only re-types the API to `Task`.
 */
export function useOptimisticTaskOverrides(rawTasks: Task[] | undefined): {
  optimisticTaskOverrides: Map<string, Partial<Task>>
  setOptimisticTaskOverride: (taskId: string, partial: Partial<Task>) => void
  applyTaskOverride: (task: Task) => Task
} {
  const { optimisticOverrides, setOverride, apply } = useOptimisticEntityOverrides<Task>(rawTasks)
  // Stable adapters — re-create only when the underlying primitives change.
  return useMemo(() => ({
    optimisticTaskOverrides: optimisticOverrides,
    setOptimisticTaskOverride: setOverride,
    applyTaskOverride: apply,
  }), [optimisticOverrides, setOverride, apply])
}
